import React from "react";
import { Box, Typography, Container, Button } from "@mui/material";

const BulkEmailValidatorContent = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Container maxWidth="md" sx={{ textAlign: "center", mt: 4 }}>
      {/* Headline */}
      <Typography variant="h4" fontWeight="bold" sx={{ fontSize: "1.75rem" }} gutterBottom>
        Instantly Validate Your Email List at Scale
      </Typography>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Upload Your CSV, Get Verified Results via Email
      </Typography>

      {/* Problem & Solution */}
      <Typography variant="body1" paragraph>
        Manually checking emails for typos or validity is tedious and impractical, especially for large lists. It leads to high bounce rates and damaged sender reputations.
      </Typography>
      <Typography variant="body1" paragraph sx={{ fontWeight: 'bold', color: 'primary.main' }}>
        Our Bulk Email Validator processes your uploaded CSV and returns a clean, reliable email list – verified for syntax, MX, SMTP, and more.
      </Typography>

      {/* Benefits */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={3}>
        Why Use Our Bulk Email Validator?
      </Typography>
      <ul style={{ textAlign: "left", display: "inline-block", listStyleType: 'none', paddingLeft: 0 }}>
        <li>📁 <strong>CSV Upload:</strong> One-click upload for bulk validation</li>
        <li>🔍 <strong>Deep Validation:</strong> Syntax, MX records, SMTP checks</li>
        <li>🚫 <strong>Filter Out:</strong> Typos, Disposable Emails, Invalid Addresses</li>
        <li>📥 <strong>Fast Results:</strong> Receive cleaned CSV by email in minutes</li>
        <li>🔐 <strong>Secure:</strong> GDPR-compliant, data not stored after processing</li>
      </ul>

      {/* How It Works */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        How It Works in 4 Simple Steps
      </Typography>
      <Box sx={{ 
        backgroundColor: '#f5f5f5', 
        p: 3, 
        borderRadius: 2,
        textAlign: 'left',
        display: 'inline-block'
      }}>
        <ol>
          <li><strong>Prepare</strong> a CSV file with one email per line</li>
          <li><strong>Upload</strong> your file and click “Validate”</li>
          <li><strong>We Process</strong> and check all emails in the background</li>
          <li><strong>Receive</strong> a cleaned CSV in your inbox within few minutes</li>
        </ol>
      </Box>

      {/* Social Proof */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        Trusted by Marketers & SaaS Teams
      </Typography>
      <Box sx={{ fontStyle: 'italic', mb: 3 }}>
        <Typography paragraph>
          "This validator helped us clean a list of 15,000 emails before our campaign – bounce rate dropped dramatically."
        </Typography>
        <Typography>- Anjali S., Growth Marketer</Typography>
      </Box>

      {/* FAQ Section */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Frequently Asked Questions
      </Typography>

      <Box textAlign="left" sx={{ display: 'inline-block' }}>
        <Typography variant="body1" fontWeight="bold">📁 What kind of file should I upload?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          A simple CSV file with one email per line. No headers required.
        </Typography>

        <Typography variant="body1" fontWeight="bold">📬 How will I receive the results?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          You'll get an email with the verified results attached as a CSV file.
        </Typography>

        <Typography variant="body1" fontWeight="bold">⏱️ How long does it take?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Typically between 30 minutes to 3 hours, depending on your list size.
        </Typography>
      </Box>

      {/* CTA */}
      <Box mt={4} mb={6}>
        <Typography variant="h6" paragraph sx={{ fontWeight: 'bold' }}>
          Ready to validate your email list?
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          size="large" 
          onClick={scrollToTop}
          sx={{ 
            fontSize: '1.1rem',
            py: 1.5,
            px: 4,
            fontWeight: 'bold'
          }}
        >
          Upload Your Email List →
        </Button>
        <Typography variant="caption" display="block" mt={1}>
          1 credit per CSV upload (up to your plan's limit)
        </Typography>
      </Box>
    </Container>
  );
};

export default BulkEmailValidatorContent;
